import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import { useIntegrityBlockGuard } from 'hooks/useIntegrityBlockGuard';
import { useInViewport } from 'hooks/useInViewport';

import MediaImage from 'components/SaloonLobby/MediaImage';
import { Tournament, TournamentStatus } from 'components/SaloonLobby/types';

import RegisteredBanner from './RegisteredBanner';
import RestrictedOverlay from './RestrictedOverlay';
import { shallowEqualRecord } from './shallowEqualRecord';
import { useTileShow } from './tileShowClock';

import styles from './GameTile.module.scss';

interface TournamentTileProps {
  tournament: Tournament;
  onClick?: (id: string) => void;
  // EN-10064: the viewer can't register for this tournament (a flagged
  // opponent is registered). When set, the marker shows AND the tap is blocked
  // (the integrity modal opens instead of the tournament detail); the engine
  // registration gate remains the authoritative fallback.
  restricted?: boolean;
  // Tile density (lobby Card Size preference). 'compact' shrinks the tile so
  // more events fit per day column; 'large' is the original. Defaults to
  // 'large' so existing callers/tests are unaffected.
  size?: 'compact' | 'large';
}

const TITLE_DEFAULT_SIZE = 20;
// 10px floor — small but legible for a bold proper-noun. Most titles
// land between 14-18px after auto-fit; the floor only matters for
// 40+ char outliers.
const TITLE_MIN_SIZE = 10;

// Shrink the tournament title font 1px at a time until the text fits
// on one line in its grid cell, or the min size is hit. If even the
// floor isn't small enough (50+ char titles in a narrow body), drop
// nowrap and let the title wrap to a second line at 10px — better
// than clipping mid-word.
const useAutoFitTitle = (text: string, enabled = true) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Compact uses a fixed CSS font-size + ellipsis (see .tournamentTile.compact
    // .title), so skip the shrink-to-fit and clear any inline size a prior
    // large render left behind (size can toggle at runtime via the pref).
    if (!enabled) {
      el.style.fontSize = '';
      el.style.whiteSpace = '';
      return;
    }
    const cell = el.parentElement;
    if (!cell) return;

    const fit = () => {
      // Reset to default before each measurement — without this, a
      // previously-shrunk title would be measured at its shrunk size
      // and never grow back when the container expands.
      let size = TITLE_DEFAULT_SIZE;
      el.style.fontSize = `${size}px`;
      el.style.whiteSpace = 'nowrap';

      const available = el.clientWidth;
      if (available <= 0) return;

      while (el.scrollWidth > available && size > TITLE_MIN_SIZE) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }

      // Hit the floor and STILL overflowing horizontally? Drop nowrap
      // so the title wraps to 2 lines at the smallest size. The tile's
      // body has ~136px of vertical room, plenty to absorb a 2-line
      // title (~24px) without clipping the rows below.
      if (el.scrollWidth > available) {
        el.style.whiteSpace = 'normal';
      }
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(cell);
    return () => observer.disconnect();
  }, [text, enabled]);

  return ref;
};

// Buy-in formatter: shows FREE for zero entries (the tournament is genuinely free).
const formatCurrency = (value: number): string => {
  if (value === 0) return 'FREE';
  if (Number.isInteger(value)) return `$${value.toLocaleString()}`;
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

// Prize pool formatter: always shows a dollar amount — "FREE" doesn't make
// sense for a pool ($0 is a valid pool value before any entries).
const formatMoney = (value: number): string => {
  if (Number.isInteger(value)) return `$${value.toLocaleString()}`;
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const STATUS_LABELS: Record<TournamentStatus, string> = {
  A: 'Announced',
  R: 'Registering',
  P: 'In Progress',
  F: 'Finished',
  C: 'Cancelled'
};

// Special-event variants — mirror the keyword/type rules from the design
// (Skullcrusher mobile-app.jsx MTourneyCard). Title keywords win first so a
// "Sunday Midnight Madness Freezeout" still gets the midnight treatment.
// `tumble` is the catch-all for hold'em-style tournaments that don't match
// anything more specific — gives them the tumbleweed roll-through.
type TournamentVariant =
  | 'midnight'
  | 'highnoon'
  | 'sunrise'
  | 'freezeout'
  | 'satellite'
  | 'freeroll'
  | 'bounty'
  | 'tumble'
  | null;

const detectTournamentVariant = (
  title: string,
  type: Tournament['type'],
  guarantee: number
): TournamentVariant => {
  const t = (title || '').toLowerCase();
  const isFreeroll = type === 'Freeroll';

  // Title-keyed variants — kept in sync with tournamentBannerFor in
  // SaloonLobbyContainer.tsx so a tile's animation matches its banner art.
  // Strict substrings ("midnight madness turbo", "high noon") so generic
  // "Midnight" or "Noon" titles don't accidentally trigger the variant.
  if (t.includes('midnight madness turbo')) return 'midnight';
  if (t.includes('high noon')) return 'highnoon';

  // Freeroll-by-guarantee. A $500 freeroll gets the sunrise animation (matches
  // the morning-freeroll banner). Everything at $1,000 and above gets the
  // meadow-green freeroll surface: this was an exact `=== 1000` match, which
  // silently dropped every bigger freeroll ($5,000, the $9,500 9-to-5) to the
  // generic tumbleweed. A threshold means new freeroll amounts keep the
  // treatment without another code change.
  if (isFreeroll && guarantee === 500) return 'sunrise';
  if (isFreeroll && guarantee >= 1000) return 'freeroll';

  // Type-keyed fallbacks. Bounty covers both regular bounties and "Big
  // Bounty" titled tournaments — they share the same bullet-hole anim,
  // just with the dedicated big-bounty banner art on top.
  if (type === 'Bounty') return 'bounty';
  if (type === 'Freezeout') return 'freezeout';
  if (type === 'Satellite') return 'satellite';
  // Tumbleweed default for plain Hold'em-style tournaments (Re-Entry +
  // anything else that lands on banner-nlh). Catch-all freerolls without
  // a recognized guarantee also fall here — no themed treatment, but the
  // banner-nlh art still renders cleanly under the default parchment.
  if (type === 'Re-Entry' || type === 'NLH' || isFreeroll) return 'tumble';
  return null;
};

// Bullet-hole positions across the banner area, with staggered delays so
// the holes appear in sequence rather than all at once. Mirrors the
// design's MTourneyCard bullet placements (mobile-app.jsx). Positions
// are percentages so they scale with the banner regardless of tile size.
const BOUNTY_BULLETS: Array<{ top: string; left: string; delay: string }> = [
  { top: '28%', left: '62%', delay: '0s' },
  { top: '58%', left: '42%', delay: '0.4s' },
  { top: '38%', left: '80%', delay: '0.85s' },
  { top: '72%', left: '68%', delay: '1.3s' },
  { top: '18%', left: '90%', delay: '1.8s' }
];

// Frost crack polylines — verbatim from the design (Skullcrusher
// mobile-app.jsx MTourneyCard). All 10 cracks emanate from the impact
// point (160, 78) in the 320×156 viewBox and zig-zag outward in different
// directions. Many vertices + sharp sideways kinks make each crack read
// as a jagged splinter instead of a smooth diagonal.
// Cracks c9 and c10 get a thinner stroke via SCSS so the pattern has
// visual variation rather than a uniform "all the same weight" look.
const FROST_CRACK_POINTS: string[] = [
  '160,78 152,68 162,58 154,46 162,32 156,18 144,8 138,-4',
  '160,78 174,72 180,58 200,52 214,40 230,32 246,18 264,4',
  '160,78 178,82 192,76 210,80 232,72 248,76 270,72 286,78',
  '160,78 178,88 190,98 208,104 222,116 240,124 256,138 270,156',
  '160,78 158,92 166,104 158,118 168,132 158,144 164,156',
  '160,78 144,88 130,96 116,108 100,116 84,128 70,140 58,156',
  '160,78 144,82 128,76 110,82 92,74 70,80 50,76 30,82',
  '160,78 142,72 130,62 116,54 100,46 84,36 70,26 56,14',
  '160,78 170,68 178,58 188,46 196,32 204,18 210,2',
  '160,78 150,90 142,102 134,116 126,128 120,140 116,156'
];

const TournamentTile: React.FC<TournamentTileProps> = ({
  tournament,
  onClick,
  restricted = false,
  size = 'large'
}) => {
  const isCompact = size === 'compact';
  // Compact pins the title to a fixed size with ellipsis (per the reference);
  // large keeps the shrink-to-fit autofit.
  const titleRef = useAutoFitTitle(tournament.title, !isCompact);
  const blockIntegrity = useIntegrityBlockGuard();
  // EN-10064: a restricted event is blocked at the tap — open the integrity
  // modal instead of the tournament detail. No waitlist for tournaments; the
  // engine registration gate is the authoritative fallback.
  const handleTileClick = () => {
    if (restricted) {
      blockIntegrity(tournament.id, 'tournament');
      return;
    }
    onClick?.(tournament.id);
  };
  // The theme show plays only in the shared clock's window for this tile's
  // theme (tileShowClock), and only for a tile on screen when the window
  // opens: one scrolled in mid-window waits for its theme's next turn rather
  // than starting a show the window's close would cut off. Off-screen, or
  // outside its window, the tile is `paused` — its animations cancelled and
  // their layers released. The window is for the tile's CURRENT variant: if a
  // broadcast changes it mid-window, the window is no longer its own and the
  // show stops.
  const tileRef = useRef<HTMLButtonElement>(null);
  const inView = useInViewport(tileRef);
  const { reduceAnimations } = useAnimationPreferences();
  const variant = detectTournamentVariant(tournament.title, tournament.type, tournament.guarantee);
  const ownShowId = useTileShow(variant, inView && !reduceAnimations);
  const [playingShow, setPlayingShow] = useState(false);
  useEffect(() => {
    setPlayingShow(ownShowId !== null && inView);
    // Only a window opening or closing starts or ends a show — not inView.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownShowId]);
  useEffect(() => {
    if (!inView) setPlayingShow(false);
  }, [inView]);
  const isCancelled = tournament.status === 'C';
  const isFinished = tournament.status === 'F';
  const isInProgress = tournament.status === 'P';
  const isRegistered = tournament.isRegistered;
  // Satellites: use the pre-computed seat count from the broadcast, not the
  // raw `guarantee` (which is dollars). Pluralize with `> 1` so both 0 and
  // 1 render as "Seat".
  const seatCount = tournament.satelliteSeats || 0;
  const seatLabel = seatCount > 1 ? 'Seats' : 'Seat';

  // Glow on a tournament tile only when the player is registered AND the
  // tournament is currently in progress (status P). Tournaments register-
  // able hours/days in advance would otherwise light up the whole rail —
  // product wants the glow reserved for "this one's running right now and
  // you're in it". SnGs use a looser rule (just isRegistered) since their
  // lifecycle from register → play is short; that logic lives in
  // SaloonLobby's playerGameIds builder.
  const isActiveForPlayer = isRegistered && tournament.status === 'P';

  // Special-event variant drives per-tile chrome + animations (shooting
  // stars on Midnight Madness, sun pulse on High Noon, frost cracks on
  // Freezeout, etc.). Falls back to no variant for any tournament that
  // doesn't match the design's recognized keywords/types.
  const variantClassMap: Record<NonNullable<TournamentVariant>, string> = {
    midnight: styles.tourneyMidnight,
    highnoon: styles.tourneyHighnoon,
    sunrise: styles.tourneySunrise,
    freezeout: styles.tourneyFreezeout,
    satellite: styles.tourneySatellite,
    freeroll: styles.tourneyFreeroll,
    bounty: styles.tourneyBounty,
    tumble: styles.tourneyTumble
  };
  const variantClass = variant ? variantClassMap[variant] : '';

  const tileClasses = [
    styles.tile,
    styles.tournamentTile,
    variantClass,
    size === 'compact' ? styles.compact : '',
    isActiveForPlayer ? styles.active : '',
    isCancelled ? styles.cancelled : '',
    isFinished ? styles.finished : '',
    restricted ? styles.restrictedTile : '',
    playingShow ? '' : styles.paused
  ]
    .filter(Boolean)
    .join(' ');

  const pillLabel = STATUS_LABELS[tournament.status];
  const pillClass = styles[`status_${tournament.status}`];

  // Rows 4 and 5 are full-width flex rows (.pillRow / .seatsRow) rather than
  // plain grid cells, at BOTH sizes. The info grid is `1fr auto`, so its right
  // column is sized by its widest cell — a satellite's "N Seats GTD" — which
  // starved the left column, wrapped the type pill and pushed the rest of the
  // layout past the tile's fixed height. Spanning each row full width lets the
  // two rows size independently of one another and of rows 2-3.
  //
  // Compact hit this first (EN-10178) because its body is narrowest, but Large
  // had no slack either: it fit at roughly 400px of viewport and overflowed
  // below that, which is what cut the tiles off on small screens with Lobby
  // Tiles Size = Large.
  const typePillEl = <div className={styles.typePill}>{tournament.typeLabel}</div>;
  const rightPillEl = tournament.isSatellite ? (
    <div className={styles.satellitePill}>Satellite</div>
  ) : (
    <div className={styles.poolLabel}>Prize Pool</div>
  );
  const seatsEl = (
    <div className={styles.seats}>
      {isCompact ? <PersonIcon className={styles.tcSeatsIcon} aria-hidden /> : '👤 '}
      {tournament.registered}
    </div>
  );
  const poolValueEl = (
    <div className={styles.poolValue}>
      {tournament.isSatellite ? `${seatCount} ${seatLabel} GTD` : formatMoney(tournament.prizePool)}
    </div>
  );

  // The button is wrapped in a thin, unclipped layer (.tileWrap) that hosts the
  // active-table glow so it can pulse via opacity instead of animating the
  // tile's box-shadow. See GameTile.module.scss / game-client-performance.md.
  return (
    <div
      className={`${styles.tileWrap} ${reduceAnimations ? styles.reduced : ''} ${
        isActiveForPlayer ? styles.tileWrapActive : ''
      } ${playingShow ? '' : styles.paused}`}>
      <button ref={tileRef} type='button' className={tileClasses} onClick={handleTileClick}>
        {/* Hide the "Registered" banner once the tournament is in progress —
            the "In Progress" status pill already conveys it's running, and a
            registered player doesn't need the pre-start banner anymore. It's
            still shown on Finished / Cancelled tiles so the player can see they
            were in it. */}
        {isRegistered && !isInProgress && <RegisteredBanner />}
        <div className={styles.banner}>
          <MediaImage
            path={`images/lobby/${tournament.banner}`}
            alt={tournament.title}
            className={styles.bannerImage}
            eager
          />
          <span className={`${styles.statusPill} ${pillClass || ''}`}>{pillLabel}</span>
        </div>

        {/* Per-variant overlays are positioned at the TILE root (not inside
            `.banner`) so they span the full tile rather than just the 156px
            banner column. The tile itself has `overflow: hidden`, so anything
            off-edge still gets clipped. Each variant's elements are pure
            decoration (aria-hidden) and absolutely positioned via SCSS. */}
        {variant === 'midnight' && <span className={styles.midnightStar} aria-hidden='true' />}
        {variant === 'highnoon' && <span className={styles.highnoonSun} aria-hidden='true' />}
        {variant === 'sunrise' && (
          <>
            <span className={styles.sunriseDim} aria-hidden='true' />
            <span className={styles.sunriseSun} aria-hidden='true' />
          </>
        )}
        {variant === 'satellite' && (
          <>
            <span
              className={`${styles.satelliteWave} ${styles.satelliteWave1}`}
              aria-hidden='true'
            />
            <span
              className={`${styles.satelliteWave} ${styles.satelliteWave2}`}
              aria-hidden='true'
            />
            <span
              className={`${styles.satelliteWave} ${styles.satelliteWave3}`}
              aria-hidden='true'
            />
          </>
        )}
        {variant === 'freezeout' && (
          <svg
            className={styles.frostCracks}
            viewBox='0 0 320 156'
            preserveAspectRatio='none'
            aria-hidden='true'>
            {/* Central impact — bright white core that flashes the moment the
                cracks appear, then fades over the 48s cycle. Pulses + scales
                independently of the cracks (own keyframe). */}
            <circle className={styles.frostImpact} cx='160' cy='78' r='6' />
            {FROST_CRACK_POINTS.map((points, i) => (
              <polyline
                key={i}
                className={`${styles.frostCrack} ${styles[`frostCrack${i + 1}`]}`}
                points={points}
              />
            ))}
          </svg>
        )}
        {variant === 'tumble' && <span className={styles.tumbleweed} aria-hidden='true' />}
        {variant === 'bounty' && (
          <>
            {BOUNTY_BULLETS.map((b, i) => (
              <span
                key={i}
                className={styles.bulletHole}
                style={{ top: b.top, left: b.left, animationDelay: b.delay }}
                aria-hidden='true'
              />
            ))}
          </>
        )}

        <div className={styles.body}>
          {/* 2-column × 5-row CSS Grid (Skullcrusher .m-tc-grid spec): title
              on row 1 col 1, then date+buyInLabel on row 2, buyInAmount on
              row 3 right, typePill+poolLabel on row 4, seats+poolAmount on
              row 5. Source order matches the design's component template. */}
          <div className={styles.tcGrid}>
            <div ref={titleRef} className={styles.title}>
              {tournament.title}
            </div>
            <div className={styles.dateTime}>{tournament.startTime}</div>
            <div className={styles.buyInLabel}>Buy-in</div>
            <div className={styles.buyInAmount}>{formatCurrency(tournament.buyIn)}</div>
            <div className={styles.pillRow}>
              {typePillEl}
              {rightPillEl}
            </div>
            <div className={styles.seatsRow}>
              {seatsEl}
              {poolValueEl}
            </div>
          </div>
        </div>
        {restricted && <RestrictedOverlay scope='tournament' />}
      </button>
    </div>
  );
};

// The tournament tab can render a few hundred tiles (multiple events/day across
// a multi-week window). A registration-count broadcast re-renders the parent;
// memo keeps unchanged tiles from re-running their render + autofit work. The
// comparator compares `tournament` by value because the broadcast hands fresh
// objects each time. `onClick` is ignored — the carousel passes a stable
// id-based handler, and the tile only forwards `tournament.id` to it.
const propsAreEqual = (
  prev: Readonly<TournamentTileProps>,
  next: Readonly<TournamentTileProps>
): boolean =>
  prev.restricted === next.restricted &&
  prev.size === next.size &&
  shallowEqualRecord(prev.tournament, next.tournament);

export default memo(TournamentTile, propsAreEqual);
